import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { changeAppLanguage, writeStoredLocale, type SupportedLocale } from "@patient-watch/i18n";
import { userLocaleQueryOptions } from "./api/selectUserLocale";
import {
    getLatestPreferredLocaleIntent,
    isPreferredLocaleWriteCurrent,
    notePreferredLocaleIntent,
} from "./preferredLocaleWriteIntent";

interface PreferredLocaleWriteResult {
    locale: SupportedLocale;
    seq: number;
}

export function usePreferredLocaleMutation(userId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (locale: SupportedLocale): Promise<PreferredLocaleWriteResult> => {
            const seq = userId ? notePreferredLocaleIntent(userId, locale) : 0;
            if (!userId) return { locale, seq };

            // Persist the latest intent at write time so a slow sync(en-GB) cannot
            // overwrite a newer LanguageSwitcher choice (de-DE / da-DK).
            const toWrite = getLatestPreferredLocaleIntent(userId) ?? locale;
            const { error } = await supabaseClient
                .from("users")
                .update({ preferred_locale: toWrite })
                .eq("user_id", userId);

            if (error) throw error;
            return { locale: toWrite, seq };
        },
        onSuccess: async ({ locale, seq }) => {
            if (userId && seq > 0 && !isPreferredLocaleWriteCurrent(userId, seq)) {
                return;
            }
            writeStoredLocale(locale);
            if (userId) {
                queryClient.setQueryData(userLocaleQueryOptions(userId).queryKey, locale);
                await queryClient.invalidateQueries(userLocaleQueryOptions(userId));
                await queryClient.invalidateQueries({ queryKey: ["diaries", "detail"] });
            }
            await changeAppLanguage(locale);
        },
    });
}
